import {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import {
  GoogleMap, useJsApiLoader, PolygonF, CircleF,
} from '@react-google-maps/api';
import MapWidgetSkeleton from '../MapWidgetSkeleton';
import { Geofence } from '../../../../API/types/Geofences';
import { isCirclePolygon, getCircleParams } from '../helpers';
import { darkStyles, lightStyles } from '../styles';
import { GEOFENCE_STYLE, INITIAL_CENTER, DRAWER_MANAGER_OPTIONS } from '../constants';
import S from './GeofenceMapStyles';
import SearchBox from '../SearchBox/SearchBox';
import { useGeofenceTerraDraw } from './useGeofenceTerraDraw';
import { useAppTheme } from '../../../../theme/ThemeContext';

const baseOptions = {
  streetViewControl: false,
  mapTypeControl: true,
  mapTypeControlOptions: {
    mapTypeIds: ['roadmap', 'satellite', 'hybrid'],
    position: 7,
  },
  fullscreenControl: false,
  minZoom: 2,
  maxZoom: 19,
};

const libs = ['places', 'geometry'];
type Props = {
  geofencesList: any;
  currentGeofence: any;
  onCircleComplete?: (circle: google.maps.Circle) => void;
  onPolygonComplete?: (circle: google.maps.Polygon) => void;
};

function fitMapToPolygons(map: google.maps.Map, polygons: number[][][]): void {
  const bounds = new google.maps.LatLngBounds();
  polygons.forEach((polygon) => {
    polygon.forEach((point) => {
      bounds.extend(new google.maps.LatLng(point[1], point[0]));
    });
  });
  map.fitBounds(bounds);
}

function GeofenceMap({
  currentGeofence,
  geofencesList = [],
  onCircleComplete,
  onPolygonComplete,
}: Props): JSX.Element {
  const { themeMode } = useAppTheme();
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const mapOptions = useMemo(() => ({
    ...baseOptions,
    styles: themeMode === 'light' ? lightStyles : darkStyles,
  }), [themeMode]);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY as string,
    libraries: libs as any,
  });

  const handleCircleComplete = useCallback((circle: google.maps.Circle): void => {
    circle.setMap(null);
    if (onCircleComplete) {
      onCircleComplete(circle);
    }
  }, [onCircleComplete]);

  const handlePolygonComplete = useCallback((polygon: google.maps.Polygon): void => {
    polygon.setMap(null);
    if (onPolygonComplete) {
      onPolygonComplete(polygon);
    }
  }, [onPolygonComplete]);

  const {
    activeDrawMode,
    startCircleDraw,
    startPolygonDraw,
    isReady,
  } = useGeofenceTerraDraw({
    map,
    onCircleComplete: handleCircleComplete,
    onPolygonComplete: handlePolygonComplete,
  });

  useEffect(() => {
    if (map && geofencesList?.results?.length) {
      const path = geofencesList?.results?.map((g: Geofence) => g.location?.coordinates[0]);
      fitMapToPolygons(map, path);
    }
  }, [geofencesList, map, isLoaded]);

  if (!isLoaded) {
    return <MapWidgetSkeleton />;
  }

  const handleMapLoad = (mapRef: google.maps.Map): void => {
    setMap(mapRef);
  };

  const handlePlaceSelected = (place: google.maps.places.PlaceResult): void => {
    if (place.geometry) {
      const { location } = place.geometry;
      const lat = location?.lat();
      const lng = location?.lng();
      if (map && lat && lng) {
        map.setCenter({ lat, lng });
        map.setZoom(12);
      }
    }
  };

  return (
    <S.Wrapper className="map-wrapper">
      <SearchBox onPlaceSelected={handlePlaceSelected} />
      <div className="geofence-draw-controls">
        <button
          type="button"
          title="Draw circle"
          aria-label="Draw circle"
          className={`geofence-draw-button geofence-draw-circle${activeDrawMode === 'circle' ? ' active' : ''}`}
          disabled={!isReady}
          onClick={startCircleDraw}
        />
        <button
          type="button"
          title="Draw polygon"
          aria-label="Draw polygon"
          className={`geofence-draw-button geofence-draw-polygon${activeDrawMode === 'polygon' ? ' active' : ''}`}
          disabled={!isReady}
          onClick={startPolygonDraw}
        />
      </div>
      <GoogleMap
        mapContainerClassName="map-container"
        center={INITIAL_CENTER}
        zoom={baseOptions.minZoom}
        options={mapOptions}
        onLoad={handleMapLoad as VoidFunction}
      >
        {(geofencesList?.results?.map((g: Geofence) => {
          const geofence = g.location?.coordinates?.[0];

          if (isCirclePolygon(g)) {
            const { center, radius } = getCircleParams(g);
            return (
              <CircleF
                key={`circle-${g._id}`}
                center={center}
                radius={radius}
                options={GEOFENCE_STYLE}
              />
            );
          }

          const path = geofence?.map((p: any) => ({ lat: p[1], lng: p[0] }));
          return (
            <PolygonF
              path={path}
              key={`polygon-${g._id}`}
              options={GEOFENCE_STYLE}
            />
          );
        }))}
        {!!(currentGeofence.geometryType === 'Circle' && currentGeofence.lat && currentGeofence.lon && currentGeofence.radius) && (
          <CircleF
            center={{ lat: +currentGeofence.lat, lng: +currentGeofence.lon }}
            radius={+currentGeofence.radius}
            options={DRAWER_MANAGER_OPTIONS}
          />
        )}
        {currentGeofence.geometryType === 'Polygon'
          && Array.isArray(currentGeofence?.polygonCoordinates)
          && currentGeofence?.polygonCoordinates?.length > 0 && (
            <PolygonF
              path={currentGeofence?.polygonCoordinates?.map(
                (coord: [number, number]) => ({ lat: coord[1], lng: coord[0] }),
              )}
              options={DRAWER_MANAGER_OPTIONS}
            />
        )}
      </GoogleMap>
    </S.Wrapper>
  );
}

export default GeofenceMap;
